<template>
    <div class="task-outcome-manager">
        <DsModal
            v-if="displayManager"
            center
            show-back
            size="lg"
            :is-open="isOpen"
            :title="$t('funnel.campaign.task.outcome.title')"
            @back="closeModal"
        >
            <template #header>
                <DsFilledButton
                    :disabled="outcomeLimitReached"
                    data-qa="add-item-button"
                    @click="addItem"
                >
                    {{ addItemLabel }}
                </DsFilledButton>
            </template>
            <div class="task-outcome-manager-table">
                <DsInlineAlert
                    v-if="outcomeLimitReached"
                    leading-icon
                    class="limit-reached-alert"
                    type="critical"
                >
                    {{ $t('funnel.campaign.task.outcome.limit') }}
                </DsInlineAlert>
                <DsTabset id="task-outcome-manager">
                    <DsTab
                        :label="$t('funnel.campaign.task.outcome.outcome.plural')"
                        :action="() => showCategoryList = false"
                        :selected="!showCategoryList"
                    >
                        <section>
                            <ItemList
                                :item-data-list="taskOutcomeList"
                                :config-data="outcomeListConfigData"
                                :loading="loadingData"
                                @edit-item="editOutcome"
                                @delete-item="deleteOutcome"
                            />
                        </section>
                    </DsTab>
                    <DsTab
                        :label="$t('funnel.campaign.task.outcome.category.plural')"
                        :action="() => showCategoryList = true"
                        :selected="showCategoryList"
                    >
                        <section>
                            <ItemList
                                :item-data-list="taskOutcomeCategoryList"
                                :config-data="outcomeCategoryListConfigData"
                                :loading="loadingData"
                                @edit-item="editOutcomeCategory"
                                @delete-item="deleteOutcomeCategory"
                            />
                        </section>
                    </DsTab>
                </DsTabset>
            </div>
            <DsConfirmDialog
                :is-open="showDeleteDialog"
                :title="deleteDialogTitle"
                :message="deleteDialogMessage"
                :confirm-label="$t('funnel.campaign.task.outcome.delete.yes')"
                :cancel-label="$t('funnel.campaign.task.outcome.delete.no')"
                destructive
                @cancel="closeDeleteDialog"
                @confirm="confirmDeleteDialog"
            />
        </DsModal>
        <component
            :is="editorComponent"
            v-if="showEditor"
            :saved-element="selectedItem"
            :editing="isEditingElement"
            :is-open="showEditor"
            :category-list="taskOutcomeCategoryList"
            @close-editor="closeEditor"
            @close-reload="closeEditorAndReload"
        />
    </div>
</template>

<script>
import ItemList from './ItemList.vue';
import OutcomeEditor from './OutcomeEditor.vue';
import CategoryEditor from './CategoryEditor.vue';
import {
    deleteOutcome,
    deleteCategory,
    getTaskOutcomeList,
    getTaskOutcomeCategoryList,
} from '../task.outcome.api';

export const TASK_OUTCOME_LIMIT = 100;

export default {
    components: {
        CategoryEditor,
        OutcomeEditor,
        ItemList,
    },
    TASK_OUTCOME_LIMIT,
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close-config-modal'],
    data() {
        return {
            taskOutcomeList: [],
            outcomeListConfigData: [],
            taskOutcomeCategoryList: [],
            outcomeCategoryListConfigData: [],
            loadingData: false,
            showCategoryList: false,
            showDeleteDialog: false,
            showEditor: false,
            isEditingElement: false,
            selectedItem: undefined,
            displayManager: true,
            editorComponent: undefined,
        };
    },
    computed: {
        outcomeLimitReached() {
            return this.taskOutcomeList.length >= TASK_OUTCOME_LIMIT;
        },
        addItemLabel() {
            return this.showCategoryList
                ? this.$t('funnel.campaign.task.outcome.add.category')
                : this.$t('funnel.campaign.task.outcome.add.outcome');
        },
        deleteDialogTitle() {
            return this.showCategoryList
                ? this.$t('funnel.campaign.task.category.delete')
                : this.$t('funnel.campaign.task.outcome.delete');
        },
        deleteDialogMessage() {
            const prefix = this.$t('funnel.campaign.task.category.delete.prefix');
            const suffix = this.$t('funnel.campaign.task.category.delete.suffix');
            const tasksAssociated = this.selectedItem?.associatedOutcomes ?? '0';

            return this.showCategoryList
                ? `${prefix} ${tasksAssociated} ${suffix}`
                : this.$t('funnel.campaign.task.outcome.delete.summary');
        },
    },
    async mounted() {
        this.buildOutcomeListConfigData();
        this.buildOutcomeCategoryListConfigData();
        await this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loadingData = true;
            this.taskOutcomeList = await getTaskOutcomeList();
            this.taskOutcomeCategoryList = await getTaskOutcomeCategoryList();
            this.loadingData = false;
        },
        closeModal() {
            this.$emit('close-config-modal');
        },
        openDeleteDialog() {
            this.showDeleteDialog = true;
        },
        closeDeleteDialog() {
            this.showDeleteDialog = false;
            this.selectedItem = undefined;
            this.taskAssociated = '0';
        },
        closeEditor() {
            this.displayManager = true;
            this.editorComponent = undefined;
            this.selectedItem = undefined;
            this.showEditor = false;
            this.isEditingElement = false;
        },
        async closeEditorAndReload() {
            this.closeEditor();
            await this.fetchData();
        },
        openEditor(component) {
            this.editorComponent = component;
            this.displayManager = false;
            this.showEditor = true;
        },
        async confirmDeleteDialog() {
            if (this.selectedItem) {
                if (this.showCategoryList) {
                    await deleteCategory(this.selectedItem.id);
                } else {
                    await deleteOutcome(this.selectedItem.id);
                }
            }
            this.closeDeleteDialog();
            await this.fetchData();
        },
        addItem() {
            if (this.showCategoryList) {
                this.addOutcomeCategory();
            } else {
                this.addOutcome();
            }
        },
        addOutcome() {
            this.isEditingElement = false;
            this.openEditor(OutcomeEditor);
        },
        editOutcome(outcomeId) {
            this.selectedItem = this.findElementById(this.taskOutcomeList, outcomeId);
            this.isEditingElement = true;
            this.openEditor(OutcomeEditor);
        },
        deleteOutcome(outcomeId) {
            this.selectedItem = this.findElementById(this.taskOutcomeList, outcomeId);
            this.openDeleteDialog();
        },
        addOutcomeCategory() {
            this.isEditingElement = false;
            this.openEditor(CategoryEditor);
        },
        editOutcomeCategory(categoryId) {
            this.selectedItem = this.findElementById(this.taskOutcomeCategoryList, categoryId);
            this.isEditingElement = true;
            this.openEditor(CategoryEditor);
        },
        deleteOutcomeCategory(categoryId) {
            this.selectedItem = this.findElementById(this.taskOutcomeCategoryList, categoryId);
            this.openDeleteDialog();
        },
        buildOutcomeCategoryListConfigData() {
            this.outcomeCategoryListConfigData = [
                {
                    label: this.$t('funnel.campaign.task.outcome.category.label'),
                    property: 'name',
                    isNumeric: false,
                    showActions: false,
                    width: '30',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.date'),
                    property: 'created',
                    isNumeric: false,
                    showActions: false,
                    width: '25',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.category.associated'),
                    property: 'associatedOutcomes',
                    isNumeric: true,
                    showActions: false,
                    width: '30',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.actions'),
                    property: 'id',
                    isNumeric: false,
                    showActions: true,
                    width: '20',
                },
            ];
        },
        findElementById(data, elementId) {
            return data.find((item) => item.id === elementId);
        },
        buildOutcomeListConfigData() {
            this.outcomeListConfigData = [
                {
                    label: this.$t('funnel.campaign.task.outcome.outcome'),
                    property: 'name',
                    isNumeric: false,
                    showActions: false,
                    width: '25',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.category'),
                    property: 'categoryName',
                    isNumeric: false,
                    showActions: false,
                    width: '25',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.date'),
                    property: 'created',
                    isNumeric: false,
                    showActions: false,
                    width: '20',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.tasks'),
                    property: 'taskAssociated',
                    isNumeric: true,
                    showActions: false,
                    width: '25',
                },
                {
                    label: this.$t('funnel.campaign.task.outcome.list.actions'),
                    property: 'id',
                    isNumeric: false,
                    showActions: true,
                    width: '20',
                },
            ];
        },
    },
};
</script>
<style lang="scss" scoped>
.limit-reached-alert {
    width: 100%;
}
</style>

<style lang="scss">
#task-outcome-manager > .tab-content {
    position: relative;
    left: 0;
    top: 0;
    clear: both;
    visibility: visible;
}
</style>
